import React, { useMemo, useState } from "react";
import { Autocomplete as MuiAutocomplete, TextField, Grid, Typography, Chip, Checkbox } from "@mzc-pdc/ui";
import { IconErrorCircle, IconCancelCircle, IconWarningTriangle } from "@mzc-cloudplex/icons";
import { useTranslation } from "react-i18next";

// 빈 multiple 값의 참조를 고정 — 렌더마다 새 [] 를 넘기면 MUI 가 value 변경으로 인식해 listbox popper 가 재배치되며 위치가 튐
const EMPTY_MULTIPLE_VALUE = [];

export const Autocomplete = ({
    id,
    multiple = true,
    value,
    options,
    onClickOption,
    placeholder,
    error,
    helperText,
    open: externalOpen, // 외부에서 제어하는 경우
    onOpen: externalOnOpen,
    onClose: externalOnClose,
    ...rest
}) => {
    const { t } = useTranslation();

    // 내부 상태 (multiple, limitTags 동시 사용시 textfield 펼치면 옵션메뉴가 닫히는 이슈 처리때문에 사용)
    const [internalOpen, setInternalOpen] = useState(false);

    const isControlled = externalOpen !== undefined;
    const open = isControlled ? externalOpen : internalOpen;

    const defaultValue = multiple ? EMPTY_MULTIPLE_VALUE : null;
    const getEndAdornment = useMemo(() => {
        if (error) {
            if (!helperText) return <IconWarningTriangle color={"warning"} size={16} />;
            return <IconErrorCircle color={`error`} size={16} />;
        }
        return undefined;
    }, [error, helperText]);

    const handleOpen = (event) => {
        if (externalOnOpen) {
            externalOnOpen(event);
        }
        if (!isControlled) {
            setInternalOpen(true);
        }
    };

    const handleClose = (event, reason) => {
        if (externalOnClose) {
            externalOnClose(event, reason);
        }

        if (!isControlled) {
            // multiple일 때만 pointer 이벤트 무시
            if (multiple) {
                if (reason === "blur" || reason === "escape" || reason === "selectOption") {
                    setInternalOpen(false);
                }
            } else {
                setInternalOpen(false);
            }
        }
    };

    return (
        <MuiAutocomplete
            multiple={multiple}
            disableClearable={multiple}
            disableCloseOnSelect={multiple}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            value={value ?? defaultValue}
            options={options ?? []}
            renderInput={(params) => {
                return (
                    <TextField
                        {...params}
                        placeholder={value ? undefined : placeholder}
                        inputProps={{
                            ...params.inputProps,
                            value: params.inputProps?.value ?? "",
                            endAdornment: getEndAdornment,
                        }}
                    />
                );
            }}
            renderOption={(props, option) => (
                <li {...props}>
                    <>
                        {multiple && (
                            <Checkbox
                                color={"greySecondary"}
                                style={{ marginRight: 8 }}
                                checked={value?.find((x) => x === option.value) !== undefined}
                            />
                        )}
                        <>{option.label}</>
                    </>
                </li>
            )}
            isOptionEqualToValue={(option, value) => option.value === value}
            renderTags={(value, getTagProps) => {
                return value.map((option, index) => {
                    // value 에는 코드만 들어오므로 options 에서 매칭되는 항목의 다국어 label 로 표시 (문자열 options 는 t fallback)
                    const matched = options?.find((o) => (o?.value ?? o) === option);
                    const label = matched?.label ?? t(`common::label::${option}`, option);
                    return (
                        <Chip
                            size={"small"}
                            label={label}
                            {...getTagProps({
                                index,
                            })}
                            key={index}
                        />
                    );
                });
            }}
            onChange={(e, newValue) => {
                if (multiple) {
                    // options(객체)와 기존 value(코드)가 섞여 들어오므로 항상 코드 배열로 정규화
                    const normalized = (newValue ?? []).map((item) =>
                        item && typeof item === "object" ? item.value : item,
                    );
                    onClickOption(id, normalized.length ? normalized : undefined);
                } else {
                    onClickOption(id, newValue?.value);
                }
                // 입력창 reflow 후 listbox popper 재배치 유도 (popper 가 anchor 크기 변화를 미관찰)
                requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
            }}
            {...rest}
        />
    );
};
